import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'kitstop:ai:session';

const AIAssistant = () => {
  const { company } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `Olá! 👋 Sou a assistente virtual da ${company?.app_name || 'KITS TOP de ALIMENTOS'}. Posso te ajudar a escolher o kit ideal, tirar dúvidas de pagamento ou entrega. Como posso te ajudar hoje?`,
      products: [],
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text, products: [] }]);
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          company_name: company?.app_name || 'KITS TOP de ALIMENTOS',
        }),
      });
      const data = await res.json();

      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch {}
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data.response || 'Desculpe, não consegui responder agora.',
          products: data.products || [],
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: '⚠️ Tive um problema para responder. Tente novamente em instantes.', products: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const goToProduct = (id) => {
    setOpen(false);
    navigate(`/produto/${id}`);
  };

  // Oculta em rotas admin
  if (location.pathname.startsWith('/admin')) return null;

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-fab"
          aria-label="Abrir assistente"
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
        >
          <Sparkles className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 sm:w-[400px] sm:h-[600px] bg-white sm:rounded-2xl shadow-2xl flex flex-col border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm leading-tight">Assistente {company?.app_name?.split(' ')[0] || 'KITS TOP'}</p>
                <p className="text-xs text-white/80">Online • Atendimento 24h</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-assistant-close"
              aria-label="Fechar"
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%] space-y-2">
                  <div
                    className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-sm'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                    }`}
                  >
                    {m.text}
                  </div>
                  {m.products && m.products.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {m.products.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => goToProduct(p.id)}
                          data-testid={`ai-product-card-${p.id}`}
                          className="bg-white rounded-xl border border-slate-200 overflow-hidden text-left hover:border-indigo-400 hover:shadow-md transition-all group"
                        >
                          {p.image && (
                            <div className="aspect-square overflow-hidden bg-slate-100">
                              <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            </div>
                          )}
                          <div className="p-2">
                            <p className="text-xs font-medium text-slate-800 line-clamp-2 leading-tight">{p.name}</p>
                            <div className="mt-1 flex items-baseline gap-1">
                              <span className="text-sm font-bold text-emerald-600">
                                R$ {p.price.toFixed(2).replace('.', ',')}
                              </span>
                              {p.original_price && p.original_price > p.price && (
                                <span className="text-[10px] line-through text-slate-400">
                                  R$ {p.original_price.toFixed(2).replace('.', ',')}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 flex items-center gap-1 text-[10px] text-indigo-600 font-medium">
                              <ShoppingCart className="w-3 h-3" />
                              Ver kit
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-3 py-2 rounded-2xl rounded-bl-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  <span className="text-xs text-slate-500">Digitando...</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            <div className="flex items-center gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Pergunte sobre kits, preços, entrega..."
                rows={1}
                data-testid="ai-assistant-input"
                className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 max-h-24"
              />
              <Button
                size="icon"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                data-testid="ai-assistant-send"
                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              Powered by Claude AI • Suas conversas são privadas
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
